import {ADataGridFilter} from "@atiautomacao/ati-ui-library";

export interface NumericFilterDefinition {
    label: string;
}

export type NumericFilterDefinitions = Record<string, NumericFilterDefinition>;

const NUMERIC_INPUT_DISALLOWED_CHARS = /[^0-9]/g;

export interface NumericFilterSanitizationResult {
    filters: Array<ADataGridFilter>;
    sanitizedFieldLabels: Array<string>;
    wasSanitized: boolean;
}

const uniqueLabels = (labels: Array<string>): Array<string> =>
    labels.filter((label, index) => labels.indexOf(label) === index);

export const isNumericFilter = (
    filter: ADataGridFilter,
    definitions: NumericFilterDefinitions
): boolean => Boolean(definitions[filter.name]);

export const getNumericFilterLabel = (
    filterName: string,
    definitions: NumericFilterDefinitions
): string => definitions[filterName]?.label || filterName;

export const sanitizeNumericFilterValue = (value: string): string =>
    value.replace(NUMERIC_INPUT_DISALLOWED_CHARS, "");

export const sanitizeNumericFilters = (
    filters: Array<ADataGridFilter>,
    definitions: NumericFilterDefinitions
): NumericFilterSanitizationResult => {
    const sanitizedFieldLabels: Array<string> = [];

    const sanitizedFilters = filters.map((filter) => {
        if (!isNumericFilter(filter, definitions)) {
            return {...filter};
        }

        const originalValue = filter.value || "";
        const sanitizedValue = sanitizeNumericFilterValue(originalValue);

        if (sanitizedValue !== originalValue) {
            sanitizedFieldLabels.push(getNumericFilterLabel(filter.name, definitions));
        }

        return {
            ...filter,
            value: sanitizedValue,
        };
    });

    const uniqueSanitizedFieldLabels = uniqueLabels(sanitizedFieldLabels);

    return {
        filters: sanitizedFilters,
        sanitizedFieldLabels: uniqueSanitizedFieldLabels,
        wasSanitized: uniqueSanitizedFieldLabels.length > 0,
    };
};

export const buildNumericFilterSanitizationMessage = (fieldLabels: Array<string>): string => {
    if (fieldLabels.length === 0) {
        return "Use apenas números nos campos numéricos.";
    }

    if (fieldLabels.length === 1) {
        return `Use apenas números no campo ${fieldLabels[0]}.`;
    }

    return `Use apenas números nos campos: ${fieldLabels.join(", ")}.`;
};
